/**
 * Unified SurveyForm component for AD projects.
 *
 * Supports three usage modes:
 * 1. Pass a pre-built SurveyJS Model via `survey` prop (Site.V2 CaptureForms pattern)
 * 2. Pass a `surveyCode` + `apiGet` function to fetch and build the model (Auth pattern)
 * 3. Pass a `formId` + `apiGet` function to fetch from "forms" controller (Site.V2 inline forms pattern)
 *
 * Props:
 * - survey (Model)         — Pre-built SurveyJS model. If provided, surveyCode/formId are ignored.
 * - surveyCode (string)    — Code to fetch form JSON from "account" controller. Requires apiGet.
 * - formId (string|number) — Form ID to fetch form JSON from "forms" controller. Requires apiGet.
 * - apiGet (function)      — API function to fetch form data. Signature: apiGet(props) => Promise<response>
 * - controller (string)    — API controller name to use for fetching (default: auto-detected based on mode)
 * - environment (string)   — Environment identifier (optional, falls back to useParams)
 * - onComplete (function)  — Callback when survey is completed
 * - enableBooleanSlider (bool) — Whether to register/handle boolean toggle-switch (default: true)
 * - enableEndpoints (bool) — Whether to handle dynamic data endpoint loading (default: true)
 * - dropdownFallbackLabel (string) — Fallback label for ungrouped dropdown items (default: "Other")
 * - hideFirstPagePanel (bool) — When true, hides the first PAGE-level title and description (used on Register page where native fields precede the survey section). Section/panel titles are preserved. (default: false)
 */
import React, { forwardRef, useRef, useImperativeHandle, useEffect, useState } from "react";
import { Model, QuestionTextModel, getLocaleStrings } from "survey-core";
import { Survey } from "survey-react-ui";
import DOMPurify from 'dompurify';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import side-effect modules that register serializers and renderers
import "./serializers";
import "./CustomSelect";
import "./BooleanSlider";
import "./OtherGroupRenderer";

// Import styles
import "survey-core/survey-core.min.css";
import "../styles/resetSurvey.scss";

// Register custom "cssClass" property so SurveyJS preserves it when parsing JSON
import { Serializer } from "survey-core";
if (!Serializer.findProperty("question", "cssClass")) {
    Serializer.addProperty("question", { name: "cssClass", default: "" });
}
// Register on "panel" too so section-level cssClass survives JSON parsing
if (!Serializer.findProperty("panel", "cssClass")) {
    Serializer.addProperty("panel", { name: "cssClass", default: "" });
}

// Build a lookup map of question name → cssClass from raw survey JSON
function buildCssClassMap(surveyModel) {
    const map = {};
    if (!surveyModel) return map;
    try {
        const json = surveyModel.toJSON();
        if (json?.pages) {
            json.pages.forEach(page => {
                (page.elements || []).forEach(function walkElement(el) {
                    if (el.cssClass) {
                        map[el.name] = el.cssClass;
                    }
                    // Handle panels with nested elements
                    if (el.elements) {
                        el.elements.forEach(walkElement);
                    }
                });
            });
        }
    } catch (e) { /* ignore */ }
    return map;
}

// Capture forms are treated as single-page only. Any additional pages defined in the
// backend form definition are intentionally ignored — we only ever keep the first page.
// (Paging, whether inline or via a "Continue" button, is not supported.)
function keepFirstPageOnly(surveyModel) {
    if (!surveyModel || !Array.isArray(surveyModel.pages)) return;
    // Remove pages from the end so indices stay valid while iterating.
    for (let i = surveyModel.pages.length - 1; i >= 1; i--) {
        surveyModel.removePage(surveyModel.pages[i]);
    }
}

// Append the display-layout class to a question's root wrapper via
// onUpdateQuestionCssClasses so it persists through React re-renders.
// The backend emits `displayColumn` (1 or 2) only for radio, checkbox and slider-toggle
// questions (never boolean). Radio maps to a "radiogroup" question; both checkbox and
// slider-toggle map to a "checkbox" question. 1-column is the default layout so no class
// is added for it; only the 2-column case emits `two-layout-col`.
function applyDisplayLayoutClass(options) {
    const type = options.question.getType();
    if (type !== 'radiogroup' && type !== 'checkbox') return;
    const displayColumn = options.question.jsonObj?.displayColumn
        ?? options.question.getPropertyValue("displayColumn");
    // 1-column is the default layout, so only the 2-column class needs to be emitted.
    if (Number(displayColumn) === 2) {
        options.cssClasses.mainRoot = (options.cssClasses.mainRoot || "") + " two-layout-col";
    }
}


const SurveyForm = forwardRef(({
    survey,
    surveyCode,
    formId,
    apiGet,
    controller: controllerProp,
    environment: environmentProp,
    onComplete,
    enableBooleanSlider = true,
    enableEndpoints = true,
    dropdownFallbackLabel,
    hideFirstPagePanel = false
}, ref) => {
    const [surveyData, setSurveyData] = useState(survey || null);
    const cssClassMapRef = useRef({});
    const params = useParams();
    const environment = environmentProp || params?.environment;
    const { t } = useTranslation();
    const initialized = useRef(false);
    const fallbackLabel = dropdownFallbackLabel || t('register.dropdown', 'Other');

    // Override SurveyJS built-in UI strings with values from the Languages table
    const enLocale = getLocaleStrings("en");
    enLocale.optionsCaption = t('surveyjs.optionsCaption');
    enLocale.otherItemText = t('surveyjs.otherItemText');
    enLocale.noneItemText = t('surveyjs.noneItemText');
    enLocale.selectAllItemText = t('surveyjs.selectAllItemText');
    enLocale.requiredError = t('surveyjs.requiredError');
    enLocale.requiredInAllRowsError = t('surveyjs.requiredInAllRowsError');
    enLocale.numericError = t('surveyjs.numericError');
    enLocale.textMinLength = t('surveyjs.textMinLength');
    enLocale.textMaxLength = t('surveyjs.textMaxLength');
    enLocale.completingSurvey = t('surveyjs.completingSurvey');
    enLocale.loadingSurvey = t('surveyjs.loadingSurvey');
    enLocale.clearCaption = t('surveyjs.clearCaption');
    enLocale.chooseFileCaption = t('surveyjs.chooseFileCaption');

    // Determine the controller to use based on mode:
    // - formId provided → "forms" controller (Site.V2 inline forms pattern)
    // - surveyCode provided → "account" controller (Auth pattern)
    // - explicit controllerProp overrides auto-detection
    const resolvedController = controllerProp || (formId ? "forms" : "account");
    // The identifier for fetching: formId takes precedence, then surveyCode
    const resolvedIdentifier = formId || surveyCode;

    // ─── Event Handlers ───────────────────────────────────────────────

    // Applies the admin-configured section CSS class to a panel's root element.
    // onUpdatePanelCssClasses fires per-panel but shares the css object, so we
    // rebuild the container class from a captured pristine base each time to
    // avoid one panel's class leaking onto the others.
    const applyPanelCssClass = (options) => {
        const panel = options.panel;
        const cssClasses = options.cssClasses;
        // The panel root (sd-panel div, sits directly under the data-key="section_..." row)
        // is exposed either as cssClasses.panel.container (nested) or cssClasses.container (flat).
        const target = cssClasses.panel || cssClasses;
        const key = "container";

        // Capture the pristine base container class the first time we touch this panel.
        if (target.__baseContainer === undefined) {
            target.__baseContainer = target[key] || "";
        }

        const cls = panel.cssClass
            || (panel.getPropertyValue && panel.getPropertyValue("cssClass"))
            || cssClassMapRef.current[panel.name];

        let containerClass = cls ? (target.__baseContainer + " " + cls).trim() : target.__baseContainer;

        // When a section (panel) has neither a title nor a description, tag it so the
        // separator top line and header spacing can be suppressed via CSS. SurveyJS still
        // renders the header/title wrapper elements even when the text is empty, so a
        // pure-CSS :has() test can't detect this — the marker class is the reliable signal.
        const panelTitle = (panel.title || (panel.getPropertyValue && panel.getPropertyValue("title")) || "").trim();
        const panelDescription = (panel.description || (panel.getPropertyValue && panel.getPropertyValue("description")) || "").trim();
        if (!panelTitle && !panelDescription) {
            containerClass = (containerClass + " section--no-header").trim();
        }

        target[key] = containerClass;
    };

    const renderHelpPanel = (questionElement, helpTitle, helpText) => {
        // Avoid duplicate panels
        if (questionElement.querySelector('.help-panel')) return;

        const panel = document.createElement("div");
        panel.className = "help-panel";
        panel.style.display = "none";

        let panelContent = '';
        if (helpTitle) panelContent += `<p class="help-panel-title">${DOMPurify.sanitize(helpTitle)}</p>`;
        if (helpText) panelContent += `<p class="help-panel-text">${DOMPurify.sanitize(helpText)}</p>`;
        panel.innerHTML = panelContent;

        // Insert panel after the header (childNodes[0])
        const header = questionElement.childNodes[0];
        if (header && header.nextSibling) {
            questionElement.insertBefore(panel, header.nextSibling);
        } else {
            questionElement.appendChild(panel);
        }

        // Wire up toggle on the help icon button
        const helpBtn = questionElement.querySelector('.help-icon-btn');
        if (helpBtn) {
            helpBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const isVisible = panel.style.display !== 'none';
                panel.style.display = isVisible ? 'none' : 'block';
                const icon = helpBtn.querySelector('span');
                if (icon) {
                    icon.className = isVisible ? 'question-icon' : 'close-icon';
                }
            });
        }
    };

    const handleonRender = (sender, options) => {
        // Apply custom CSS class from admin (if configured on the question)
        // First try direct property (works if Serializer registration succeeded),
        // then fall back to the pre-built map from raw JSON.
        const customCssClass = options.question.cssClass 
            || options.question.getPropertyValue("cssClass")
            || cssClassMapRef.current[options.question.name];

        if (customCssClass) {
            // Add to the element — note: may be overwritten by React re-renders
            // The persistent approach uses onUpdateQuestionCssClasses (see below)
            String(customCssClass).split(/\s+/).forEach(cls => {
                if (cls) options.htmlElement.classList.add(cls);
            });
        }

        // Boolean toggle-switch class management
        if (enableBooleanSlider) {
            if (options.question.getType() === 'boolean') {
                const renderAs = options.question.jsonObj?.renderAs || options.question.getPropertyValue("renderAs");
                if (renderAs === 'toggle-switch') {
                    options.htmlElement.classList.add('custom-boolean', 'custom-boolean--toggle');
                } else {
                    options.htmlElement.classList.add('custom-boolean', 'custom-boolean--checkbox');
                }
            }
            if (options.question.getType() === 'checkbox' && options.question.jsonObj?.renderAs === 'toggle-switch') {
                options.htmlElement.classList.add('custom-boolean', 'custom-boolean--toggle');
            }
        }

        // Dynamic endpoint loading
        if (enableEndpoints && apiGet) {
            if (options.question?.jsonObj?.isCountry && options.question?.dataEndpoint) {
                handleEndpoints(options);
            }
            if (options.question?.addItem && options.question?.dataEndpoint) {
                handleEndpoints(options);
            }
            if (options.question.jsonObj?.searchable && options.question.jsonObj?.dataEndpoint) {
                handleEndpoints(options);
            }
            // Also trigger for any dropdown with a dataEndpoint (Site.V2 forms pattern)
            if (options.question.getType() === 'dropdown' && options.question.jsonObj?.dataEndpoint) {
                handleEndpoints(options);
            }

            if (options.question?.jsonObj?.dataEndpoint) {
                options.question.searchEndpoint = (term) => handleEndpoints(options, term);
            }
        }

        let required = options.question.jsonObj?.isRequired;

        // Dropdown category grouping
        if (options.question.getType() === 'dropdown') {
            const dropdownChoices = options.question.jsonObj?.choicesByCategory;
            const category = options.question.jsonObj?.choices;
            if (dropdownChoices) {
                const dropdownList = options.htmlElement.querySelectorAll("input");

                dropdownList.forEach((list) => {
                    list.addEventListener("focus", () => {
                        setTimeout(() => {
                            const popupList = options.htmlElement.querySelector(".sv-dropdown-popup .sv-list");
                            if (!popupList) return;
                            const listItems = popupList.querySelectorAll("li");
                            const groupContainers = {};
                            const categoryMap = {};

                            category?.forEach((choice) => {
                                let cat = choice.category || "";
                                if (!categoryMap[cat]) {
                                    categoryMap[cat] = [];
                                }
                                categoryMap[cat].push(choice.text);
                            });

                            popupList.innerHTML = "";

                            listItems.forEach((li) => {
                                const inputValue = li.textContent.trim();
                                if (!inputValue) return;

                                const categoryList = Object.keys(categoryMap).find((key) =>
                                    categoryMap[key].includes(inputValue)
                                ) || fallbackLabel;

                                if (!groupContainers[categoryList]) {
                                    const container = document.createElement("div");
                                    container.className = `item-container dropdown-category-group category-group-${categoryList?.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-]/g, "")}`;

                                    const header = document.createElement("h5");
                                    header.className = "module-subheading";
                                    header.textContent = categoryList;

                                    const ul = document.createElement("ul");
                                    container.appendChild(header);
                                    container.appendChild(ul);

                                    popupList.appendChild(container);
                                    groupContainers[categoryList] = ul;
                                }

                                groupContainers[categoryList].appendChild(li);
                            });
                        }, 0);
                    });
                });
            }
        }

        // Help icon and panel logic
        const helpTitle = options.question.jsonObj?.helpTitle;
        const helpText = options.question.jsonObj?.helpText;
        const hasHelp = !!(helpTitle || helpText);

        // "Optional" label + help icon injection
        // Skip for HTML questions (display-only, no mandatory/optional concept)
        // Skip if already rendered (prevents duplicates on re-render)
        if (options.question.getType() !== 'html' && !options.htmlElement.querySelector('.optional') && !options.htmlElement.querySelector('.label-aid')) {
            const optionalText = t('surveyform.optional');
            const helpAriaLabel = t('surveyform.helparialabel');

            if (!required && !hasHelp) {
                // Simple case: just "Optional" label (matches master pattern)
                const header = options.htmlElement.childNodes[0];
                if (header) {
                    header.innerHTML += `<div class="optional">${optionalText}</div>`;
                }
            } else if (!required || hasHelp) {
                // Complex case: has help icon, use label-aid wrapper
                let labelAidHtml = '<div class="label-aid">';
                if (!required) labelAidHtml += `<span class="optional">${optionalText}</span>`;
                if (!required && hasHelp) labelAidHtml += '<span class="label-aid-separator">|</span>';
                if (hasHelp) labelAidHtml += `<button type="button" class="help-icon-btn" aria-label="${helpAriaLabel}"><span class="question-icon"></span></button>`;
                labelAidHtml += '</div>';

                const header = options.htmlElement.childNodes[0];
                if (header) {
                    header.innerHTML += labelAidHtml;
                }
            }
            if (hasHelp) renderHelpPanel(options.htmlElement, helpTitle, helpText);
        }

        // Blur validation — attach to actual input/textarea elements
        let inlineForms = options.htmlElement.querySelector("input, textarea");
        if (inlineForms) {
            inlineForms.addEventListener("blur", function (event) {
                const { value } = event.target;
                setTimeout(() => {
                    if (!value) {
                        options.question.hasErrors(true);
                    }
                }, 0);
            });
        }

        if (options.question.getType() !== "checkbox" && options.question.getType() !== "radiogroup") return;

        // Skip DOM manipulation for checkbox questions rendered as toggle-switch
        // (the CheckboxSlider custom renderer handles its own layout)
        if (options.question.getType() === 'checkbox' && options.question.jsonObj?.renderAs === 'toggle-switch') return;

        // Skip if category grouping has already been applied (prevents duplication on re-render)
        if (options.htmlElement.querySelector('.category-group')) return;

        // Checkbox/radiogroup category grouping
        let category = options.question.jsonObj?.choices;
        let categoryMap = {};

        category?.forEach((choice) => {
            let cat = choice.category || "";
            if (!categoryMap[cat]) {
                categoryMap[cat] = [];
            }
            categoryMap[cat].push(choice.value);
        });

        let inputElements = options.htmlElement.querySelectorAll("input[type='radio'], input[type='checkbox']");

        inputElements.forEach((inputElement) => {
            let itemValue = inputElement.value;
            let itemParent = inputElement.closest('.sd-item');
            let instertBeforeItem = inputElement.closest('.sd-question__content');
            let categoryList = Object.keys(categoryMap).find((key) =>
                categoryMap[key].includes(itemValue)
            );

            if (categoryList) {
                let groupContainer = options.htmlElement.querySelector(`.category-group-${categoryList?.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-]/g, "")}`);

                if (!groupContainer) {
                    groupContainer = document.createElement("div");
                    groupContainer.className = `item-container category-group category-group-${categoryList?.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9\-]/g, "")}`;
                    groupContainer.innerHTML = ` <h5 class="module-subheading">${categoryList}</h5>`;
                    options.htmlElement.insertBefore(groupContainer, instertBeforeItem);
                }

                if (itemParent) {
                    groupContainer.appendChild(itemParent);
                }
            }

            // Newsletter description
            if (options.question.jsonObj?.isNewsletter && options.question.jsonObj?.isOptionDescriptionVisible) {
                let choices = options.question.jsonObj?.choices;
                let matchedChoice = choices?.find(c => c.value === itemValue);
                if (matchedChoice?.description && itemParent) {
                    if (!itemParent.querySelector('.newsletter-option-description')) {
                        let descEl = document.createElement("div");
                        descEl.className = "newsletter-option-description";
                        descEl.innerHTML = DOMPurify.sanitize(matchedChoice.description);
                        itemParent.appendChild(descEl);
                    }
                }
            }
        });
    };

    // Note: the "Other" choice item and its comment area are now paired together by a
    // dedicated custom renderer (see OtherGroupRenderer.js — CheckboxWithOtherGroup /
    // RadiogroupWithOtherGroup, registered as the default renderer for checkbox/radiogroup
    // questions). That renderer wraps both inside a single .sd-other-group div directly in
    // the render output, which survives re-renders — a plain onAfterRenderQuestion DOM
    // patch does not, since SurveyJS/React re-renders the item list independently.

    // NOTE: We intentionally do NOT rewrite question.value (or question.comment) to inject
    // adOtherValue anywhere in this file. Calling sender.setValue()/question.value= on a
    // select-base question re-enters SurveyJS's own other/comment-sync machinery
    // (QuestionSelectBase.setQuestionValue -> updateCommentFromSurvey / setOtherValueInternally),
    // which OVERWRITES the just-typed comment with whatever the new value ends up being — for
    // BOTH single-select (dropdown/radiogroup) and multi-select (checkbox) questions. In other
    // words, calling setValue(field, adOtherValue) does not just swap "other" for the real
    // other-answer code — it also clobbers the typed comment text itself. This was verified
    // directly against survey-core's runtime behaviour, not assumed.
    //
    // So the question's own value is left exactly as SurveyJS produces it natively: the
    // literal string "other" (single-select) or an array containing "other" (checkbox), and
    // the typed comment is left exactly as SurveyJS's own setComment/-Comment mechanism
    // produces it. Instead, the real "other" answer code (adOtherValue, e.g. "1001|other")
    // is exported as a SEPARATE "<field>-OtherCode" key at getSurveyData() time (see
    // getSurveyData below), so the backend can resolve the true Inform answer id without us
    // ever touching SurveyJS's internal value/comment state.

    const handleEndpoints = (options, searchTerm) => {
        if (!apiGet) return Promise.resolve([]);

        const apiProps = {
            "controller": resolvedController,
            "action": options.question.dataEndpoint,
            "identifier": resolvedIdentifier,
            "environment": environment,
            "data": ""
        };
        const q = options.question.jsonObj || options.question;

        if (q?.dataEndpoint === "getformcompanies") {
            //passing email for domain matching
            const userEmail = localStorage.getItem('email') || "";

            apiProps.data = {
                isDisplayAddrLine1: !!q.isDisplayAddrLine1,
                isDisplayPostCode: !!q.isDisplayPostCode,
                isDisplayTown: !!q.isDisplayTown,
                isDisplayCountry: !!q.isDisplayCountry,
                email: userEmail,
                searchTerm: searchTerm || "",
            };
        }

        return apiGet(apiProps)
            .then(function (response) {
                let data = response.data.choices;
                let choices;
                if (q?.dataEndpoint === "getformcompanies") {
                    // AD_CompanyLookupSearch_V3 already applies direct/indirect domain
                    // matching and ordering server-side, so the list is used as-is.
                    choices = data.map((item) => {
                        const addressInfo = [item?.AddressLine1, item?.PostCode, item?.Town, item?.Country].filter(Boolean);
                        const details = addressInfo.length > 0 ? ` (${addressInfo.join(", ")})` : "";
                        return {
                            value: item.Name,
                            text: `${item.Name}${details}`,
                        };
                    });
                } else {
                    choices = data.map((item) => ({
                        value: item.name,
                        text: item.name,
                    }));
                }

                // Preserve a manually-added company through Add New Company feature so a search
                // response can't wipe out the value the user just created.
                const createdValue = options.question.adCreatedValue;
                if (createdValue && !choices.some((c) => c.value === createdValue)) {
                    choices = [...choices, { value: createdValue, text: createdValue }];
                }

                options.question.choices = choices;
                return choices;
            })
            .catch((err) => {
                console.error("API error:", err);
                return [];
            });
    };

    // ─── Initialization ───────────────────────────────────────────────

    const initializeModel = (surveyModel) => {
        // Hide SurveyJS built-in navigation buttons (we manage navigation externally)
        surveyModel.showNavigationButtons = false;

        // Capture forms are single-page only — drop any extra pages from the definition.
        keepFirstPageOnly(surveyModel);

        // Apply custom CSS classes from admin — persists through re-renders
        surveyModel.onUpdateQuestionCssClasses.add((sender, options) => {
            const cls = options.question.cssClass 
                || options.question.getPropertyValue("cssClass") 
                || cssClassMapRef.current[options.question.name];
            if (cls) {
                options.cssClasses.mainRoot = (options.cssClasses.mainRoot || "") + " " + cls;
            }
            applyDisplayLayoutClass(options);
        });

        // Apply custom CSS classes from admin to sections (panels) — persists through re-renders
        surveyModel.onUpdatePanelCssClasses.add((sender, options) => {
            applyPanelCssClass(options);
        });

        surveyModel.onAfterRenderQuestion.add(handleonRender);

        // Render HTML in question descriptions
        surveyModel.onTextMarkdown.add((sender, options) => {
            options.html = options.text;
        });

        // Hide first page panel (title + description) when it follows the native registration form
        if (hideFirstPagePanel) {
            const firstPage = surveyModel.pages[0];
            if (firstPage) {
                // Clear only the page-level title and description so the survey blends after
                // the native registration fields. The first panel/section title is genuine
                // admin-configured content and must NOT be cleared.
                firstPage.title = '';
                firstPage.description = '';
            }
        }

        // Ensure preselected boolean toggle switches have their value set
        if (enableBooleanSlider) {
            surveyModel.getAllQuestions().forEach(q => {
                if (q.getType() === "boolean" && q.getPropertyValue("renderAs") === "toggle-switch") {
                    if (q.defaultValue === true || q.getPropertyValue("defaultValue") === true) {
                        surveyModel.setValue(q.name, q.valueTrue || true);
                    }
                }
            });
        }

        return surveyModel;
    };

    // Mode 1: Pre-built model passed via props
    // Register event handlers BEFORE the first render so onAfterRenderQuestion fires with
    // our handler attached. useEffect runs AFTER render which is too late — the initial
    // questions have already rendered and the event has already fired without our listener.
    if (survey && !initialized.current) {
        // Build cssClass lookup map from the raw model JSON
        cssClassMapRef.current = buildCssClassMap(survey);
        console.log('[CssClass] map built:', cssClassMapRef.current);

        // Hide SurveyJS built-in navigation buttons (we manage navigation externally)
        survey.showNavigationButtons = false;

        // Capture forms are single-page only — drop any extra pages from the definition.
        keepFirstPageOnly(survey);

        // Apply custom CSS classes from admin — uses onUpdateQuestionCssClasses so it persists through re-renders
        survey.onUpdateQuestionCssClasses.add((sender, options) => {
            const cls = options.question.cssClass 
                || options.question.getPropertyValue("cssClass") 
                || cssClassMapRef.current[options.question.name];
            if (cls) {
                // options.cssClasses.mainRoot is the outer wrapper div (sd-element)
                options.cssClasses.mainRoot = (options.cssClasses.mainRoot || "") + " " + cls;
            }
            applyDisplayLayoutClass(options);
        });

        // Apply custom CSS classes from admin to sections (panels) — persists through re-renders
        survey.onUpdatePanelCssClasses.add((sender, options) => {
            applyPanelCssClass(options);
        });

        survey.onAfterRenderQuestion.add(handleonRender);
        survey.onTextMarkdown.add((sender, options) => {
            options.html = options.text;
        });
        // Hide first page panel (title + description) when it follows the native registration form
        if (hideFirstPagePanel) {
            const firstPage = survey.pages[0];
            if (firstPage) {
                // Clear only the page-level title and description so the survey blends after
                // the native registration fields. The first panel/section title is genuine
                // admin-configured content and must NOT be cleared.
                firstPage.title = '';
                firstPage.description = '';
            }
        }
        initialized.current = true;
    }

    // Sync surveyData state when the survey prop arrives (Mode 1)
    useEffect(() => {
        if (survey && surveyData !== survey) {
            setSurveyData(survey);
        }
    }, [survey]);

    // Mode 2/3: Fetch form by surveyCode or formId
    useEffect(() => {
        if (resolvedIdentifier && apiGet && !survey) {
            const fetchForm = async () => {
                try {
                    const response = await apiGet({
                        "controller": resolvedController,
                        "action": "getformbyid",
                        "identifier": resolvedIdentifier,
                        "environment": environment
                    });
                    let surveyJson = response.data.surveyData;
                    const surveyModel = new Model(surveyJson);
                    initializeModel(surveyModel);
                    cssClassMapRef.current = buildCssClassMap(surveyModel);
                    setSurveyData(surveyModel);
                    initialized.current = true;
                } catch (err) {
                    console.error("Failed to load survey form:", err);
                }
            };
            fetchForm();
        }
    }, [resolvedIdentifier]);

    // onComplete handler
    useEffect(() => {
        if (onComplete && surveyData) {
            surveyData.onComplete.add(sender => onComplete(sender.data));
        }
    }, [onComplete, surveyData]);

    // ─── Imperative Handle ────────────────────────────────────────────

    useImperativeHandle(ref, () => ({
        validateSurvey: () => surveyData?.validate(),
        getSurveyData: () => {
            if (!surveyData) return surveyData?.data;
            const data = { ...surveyData.data };
            // For every "Other"-enabled select question that currently has "other"
            // selected, export the real Inform other-answer code (adOtherValue, e.g.
            // "1001|other") under a separate "<field>-OtherCode" key. The question's own
            // value/comment are left untouched (see handleValueChanged note above) so the
            // backend must read this key to resolve the true answer id instead of relying
            // on the literal "other" placeholder that ships in data[field].
            surveyData.getAllQuestions().forEach(q => {
                if (!q.isOtherSelected) return;
                if (q.adOtherValue) {
                    data[q.name + "-OtherCode"] = q.adOtherValue;
                }
                // When the question has a linked demographic configured, export its dsm_code
                // so the middleware can save the typed "Other" free-text (data[q.name +
                // "-Comment"]) to that separate custom attribute instead of only onto the
                // question itself. See AccountController.saveDemographics.
                if (q.adOtherLinkedCode) {
                    data[q.name + "-OtherLinkedCode"] = q.adOtherLinkedCode;
                }
            });
            return data;
        },
        getBooleanToggleValues: () => {
            if (!surveyData) return {};
            const result = {};
            const questions = surveyData.getAllQuestions();
            questions.forEach(q => {
                if (q.getType() === "boolean") {
                    const renderAs = q.getPropertyValue("renderAs") || q.jsonObj?.renderAs || q.renderAs;
                    if (renderAs === "toggle-switch") {
                        const hasExplicitValue = q.value !== undefined && q.value !== null;
                        let isOn = false;
                        if (hasExplicitValue) {
                            isOn = q.value === true || q.value === q.valueTrue;
                        } else {
                            isOn = q.defaultValue === true;
                        }
                        if (isOn) {
                            result[q.name] = q.valueTrue || "true";
                        }
                    }
                }
            });
            return result;
        },
        prefixAnswers: () => {
            if (!surveyData) return;
            const survey = surveyData;
            const questions = survey.getAllQuestions();

            questions.forEach(q => {
                if (q.name.startsWith("answercode_")) {
                    q.page.removeQuestion(q);
                }
            });

            questions.forEach((q) => {
                if (!q.isVisible) return;
                if (
                    ["text", "comment", "date", "datetime", "dropdown"].includes(q.getType()) &&
                    q.value &&
                    q.answerCode
                ) {
                    if (q.answerCode && q.value && ["string", "number"].includes(typeof q.value)) {
                        const page = q.page;
                        const hiddenQuestion = new QuestionTextModel("answercode_" + q.name);
                        hiddenQuestion.defaultValue = q.answerCode || "";
                        hiddenQuestion.visible = false;

                        page.addQuestion(hiddenQuestion);
                        survey.setValue(hiddenQuestion.name, hiddenQuestion.defaultValue);
                    }
                }
            });
        }
    }));

    // ─── Render ───────────────────────────────────────────────────────

    if (!surveyData) {
        return (
            <div className="survey-loader-wrapper">
                <div className="survey-loader"></div>
            </div>
        );
    }

    return (
        <Survey model={surveyData} />
    );
});

export default SurveyForm;
